import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { SubscriptionWithDestroyTriggerComponent } from 'src/app/subscription-helpers/subscription-with-destroy-trigger.component';
import { ProjectPlacement } from '../../model/project/projectPlacement';
import { ProductPlacementPickerComponent } from '../../shared/product-placement-picker/product-placement-picker.component';
import Address from '../../model/project/address';
import BrowserHelper from '../../helpers/browser-helper';
import { SubProject } from '../../model/project/sub-project';
import { Project } from 'src/app/model/project/project';
import ProjectHelper from '../../helpers/project-helper';
import { BaseProject } from '../../model/project/base-project';
import Product from 'src/app/model/product/product';
import ProductHelper from 'src/app/helpers/product-helper';
import OtherProduct from 'src/app/model/product/other-product/other-product';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import Board from 'src/app/model/product/board/board';
import { PlacementTypeChoices } from 'src/app/model/product/board/board-part';
import { CollectKeyFromOption } from '../../helpers/interfaces/collect-key-from-option';
import { DO_CHECK_DEBOUNCE_TIME } from 'src/globals/globals';
import { ProjectDisplayObservableService } from 'src/app/services/project-display-observable.service';
import { GeneralHelper } from 'src/app/helpers/general-helper';
import { GetStreetViewMetadataService } from 'src/app/services/google-maps/get-streetview-metadata.service';
import IPBoard from 'src/app/model/product/ip-board/ip-board';
import { PoleColorsChoiceArray } from 'src/app/model/sign/enums/pole-color';
import DateHelper from 'src/app/helpers/date-helper';
import { IPBoardPlacementTypeChoices } from 'src/app/model/product/ip-board/ip-board-placement-type';
import * as moment from 'moment';
import IPBoardPart from 'src/app/model/product/ip-board/ip-board-part';
import { STATUS } from '../../model/project/status';

// IMPORTANT: this component is only for baseprojects!!!!!!

@Component({
  selector: 'project-placement-picker',
  templateUrl: './project-placement-picker.component.html',
  styleUrls: ['./project-placement-picker.component.scss'],
})
export class ProjectPlacementPickerComponent
  extends SubscriptionWithDestroyTriggerComponent
  implements OnInit
{
  error = '';
  number: string;
  options: [];
  public isCollapsed = false;
  public safari: boolean;
  hideAddressFields: boolean = false;

  @Input() // nullable
  public inputProject: Project;
  @Input()
  public inputSubProject: SubProject;
  @Input()
  public inputProjectPlacement: ProjectPlacement;
  @Input()
  public client = false;
  @Input()
  public fromOldSystem = false;
  @Input()
  public disabled = false;
  @Output()
  public outputProject = new EventEmitter();
  @Output()
  public higherFloorOptionSelected = new EventEmitter<boolean>();

  @ViewChildren(ProductPlacementPickerComponent)
  productPlacementPickers: QueryList<ProductPlacementPickerComponent>;

  showGoogleMapsButton: boolean = false;
  showUndergroundPipeworkQuestion: boolean = false;
  showTopdownLocationPhoto: boolean = false;
  showFloorQuestion: boolean = false;
  showExtrasSection: boolean = false;
  checkTrigger = new EventEmitter<any>();

  addressPanoId: string;
  sitePhotosAdded: boolean = false;

  projectHasIPBoard: boolean = false;
  ipBoardForPoleColor: IPBoard;

  showAppointmentWarning: boolean = false;
  alwaysShowCollectKey: boolean = false;
  collectKeyFromOptions: CollectKeyFromOption[] = [];

  minPlacementDate: Date;
  dateError: boolean = false;

  POLE_COLORS_CHOICE_ARRAY = PoleColorsChoiceArray;

  ipBoardPartForFloorFiltering: IPBoardPart;

  constructor(
    private projectDisplayObservableService: ProjectDisplayObservableService,
    private getStreetViewMetadataService: GetStreetViewMetadataService,
    private cd: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit() {
    this.initializeMinDate();
    if (this.inputSubProject.projectPlacement == null) {
      this.inputSubProject.projectPlacement = new ProjectPlacement();
    }
    if (this.inputSubProject.projectPlacement.address == null) {
      this.inputSubProject.projectPlacement.address = new Address();
    }
    if (this.fromOldSystem) {
      this.inputSubProject.currentExecutionDate = new Date();
    }
    this.safari = BrowserHelper.isSafari();

    this.calculateIPBoardPartForFloorFiltering();

    this.initializeConditionalDisplays();
    this.initializeSubscriptions();
  }

  private initializeMinDate(): void {
    this.minPlacementDate =
      this.client &&
      (this.inputSubProject.id == null ||
        this.inputSubProject.status === STATUS.DRAFT.valueOf())
        ? DateHelper.getMomentBusinessDayMinProjectPlacement().toDate()
        : null;
  }

  private initializeConditionalDisplays(): void {
    this.calculateAddressPano();
    this.calculateSitePhotosAdded();
    this.calculateShowUndergroundPipeworkQuestion();
    this.calculateShowTopdownLocationPicture();
    this.calculateAlwaysShowCollectKey();
    this.calculateShowFloorQuestion();
    this.calculateShowExtrasSection();
    this.calculateProjectHasIPBoard();
    this.calculateIPBoardForPoleColor();
  }

  private initializeSubscriptions(): void {
    this.addSubscription(
      this.projectDisplayObservableService.getProductAddedSubscribable(),
      () => this.calculateIPBoardPartForFloorFiltering()
    );

    const showFloorAndAlwaysShowCollectKeyObservable =
      this.createObservableUntilDestroy(
        this.projectDisplayObservableService.getProductPlacementVariableChangedSubscribable(),
        this.projectDisplayObservableService.getIPBoardConfigurationChangedSubscribable()
      );

    this.addSubscription(
      this.checkTrigger,
      () => this.calculateShowUndergroundPipeworkQuestion(),
      () => {},
      DO_CHECK_DEBOUNCE_TIME
    );
    this.addSubscription(
      this.projectDisplayObservableService.getProductAddedSubscribable(),
      () => {
        this.calculateShowTopdownLocationPicture();
        this.calculateShowFloorQuestion();
        this.calculateShowExtrasSection();
        this.calculateProjectHasIPBoard();
      }
    );
    this.addSubscription(showFloorAndAlwaysShowCollectKeyObservable, () => {
      this.calculateShowFloorQuestion();
      this.calculateAlwaysShowCollectKey();
    });
    this.addSubscription(
      this.projectDisplayObservableService.getIPBoardConfigurationChangedSubscribable(),
      () => {
        this.calculateIPBoardForPoleColor();
        this.calculatePlacementFloorReset();
      }
    );
  }

  ngDoCheck() {
    this.checkTrigger.next();
  }

  getProductPlacementPictureTitle(product: Product, index: number) {
    return ProductHelper.isOtherProduct(product)
      ? ProductHelper.getType(
          product.type,
          (<OtherProduct>product).otherProductName
        ) +
          ' #' +
          (index + 1)
      : ProductHelper.isIPBoard(product)
      ? ''
      : ProductHelper.getType(product.type) + ' #' + (index + 1);
  }

  siteVisitChange(value: boolean): void {
    this.inputSubProject.projectPlacement.siteVisit = value;
    this.emitWithProject();
  }

  getBaseProject(): BaseProject {
    return ProjectHelper.getBaseProject(this.inputProject);
  }

  extraInfoAddressChange(value) {
    this.inputSubProject.projectPlacement.extraInfoAddress = value;
    this.emitWithProject();
  }

  extraSiteVisitNeedInfoChange(value) {
    this.inputSubProject.projectPlacement.extraSiteVisitNeedInfo = value;
    this.emitWithProject();
  }

  extraInfoSiteChange(value) {
    this.inputSubProject.projectPlacement.extraInfoSite = value;
    this.productPlacementPickers.forEach((e) => {
      e.inputProduct.productPlacement.extraInfoSite = value;
    });
    this.cd.detectChanges();
    this.emitWithProject();
  }

  placementFloorChange(value) {
    const mustHaveAppointment = ProjectHelper.mustHaveAppointment(
      this.inputSubProject.projectPlacement
    );
    this.emit();

    this.showAppointmentWarning = this.projectHasIPBoard && mustHaveAppointment;
  }

  appointmentChange(value) {
    this.inputSubProject.projectPlacement.appointment = value;
    this.emit();
  }

  needAppointmentInfoChange(value) {
    this.inputSubProject.projectPlacement.needAppointmentInfo = value;
    this.emit();
  }

  extraPlacementInfoChange(value) {
    this.inputSubProject.projectPlacement.extraPlacementInfo = value;
    this.emit();
  }

  placeASAP(event) {
    this.inputSubProject.projectPlacement.placeASAP = event;
    if (event) {
      delete this.inputSubProject.projectPlacement.placementEarliestDate;
    }
    this.projectDisplayObservableService
      .getProjectPlacementRequestDateChangedSubscribable()
      .next();
    this.emit();
  }

  placementEarliestDateChange(event) {
    if (
      this.minPlacementDate != null &&
      this.inputSubProject.projectPlacement.placementEarliestDate <
        this.minPlacementDate
    ) {
      this.dateError = true;
    } else {
      this.dateError = false;
      if (event != null) {
        this.inputSubProject.projectPlacement.placeASAP = false;
      }
      this.projectDisplayObservableService
        .getProjectPlacementRequestDateChangedSubscribable()
        .next();
      this.emit();
    }
  }

  setAddress(value) {
    this.inputSubProject.projectPlacement.address = value;
    this.calculateAddressPano();
    this.emitWithProject();
  }

  setKilometersTo(value) {
    this.inputProjectPlacement.kilometersTo = value;
    this.emitWithProject();
  }

  setWasValidAddress(value) {
    this.showGoogleMapsButton = value;
  }

  undergroundPipeworkChanged(value) {
    this.inputSubProject.projectPlacement.undergroundPipework = value;
    this.projectDisplayObservableService
      .getProjectPlacementVariableChangedSubscribable()
      .next();
    this.emit();
  }

  undergroundPipeworkTextChanged(value: string) {
    this.inputSubProject.projectPlacement.undergroundPipeworkText = value;
    this.emit();
  }

  unknownAddressChanged(value: boolean) {
    this.hideAddressFields = value;
    this.inputSubProject.projectPlacement.address = new Address();
    this.projectDisplayObservableService
      .getAddressWasHiddenSubscribable()
      .next(value);
  }

  collectKeyChange(value: boolean) {
    this.inputSubProject.projectPlacement.collectKey = value;
    this.projectDisplayObservableService
      .getProjectPlacementVariableChangedSubscribable()
      .next();
    this.emit();
  }

  collectKeyExtraInfoChange(value: string) {
    this.inputSubProject.projectPlacement.collectKeyExtraInfo = value;
    this.projectDisplayObservableService
      .getProjectPlacementVariableChangedSubscribable()
      .next();
    this.emit();
  }

  poleColorChanged(): void {
    this.projectDisplayObservableService
      .getProductPlacementVariableChangedSubscribable()
      .next();
    this.emit();
  }

  extraInfoInvoiceChanged(value: string): void {
    this.inputSubProject.extraInfoInvoice = value;
    this.emit();
  }

  executionAssetsChanged(product: Product): void {
    this.projectDisplayObservableService
      .getProductAssetsChangedSubscribable()
      .next({ productId: product.id, formSection: 'execution' });
  }

  oldSystemJobExecutionDateChanged(): void {
    this.projectDisplayObservableService
      .getProductExecutedSubscribable()
      .next();
  }

  emit() {
    this.outputProject.emit();
  }

  emitWithProject() {
    this.outputProject.emit(this.inputSubProject);
  }

  calculateShowUndergroundPipeworkQuestion() {
    if (
      this.inputProject == null ||
      !CollectionHelper.isPopulated(this.inputProject.products)
    ) {
      this.showUndergroundPipeworkQuestion = false;
      return;
    }
    this.showUndergroundPipeworkQuestion = this.inputProject.products.some(
      (p) => {
        if (ProductHelper.isBoard(p)) {
          const board = <Board>p;
          return (
            CollectionHelper.isPopulated(board.boardParts) &&
            board.boardParts.some(
              (bp) =>
                bp.placementType != null &&
                bp.placementType.ordinal ===
                  PlacementTypeChoices.ON_POSTS.ordinal
            )
          );
        }
        if (ProductHelper.isIPBoard(p)) {
          return ProductHelper.showUndergroundPipeworkQuestionForIPBoard(
            <IPBoard>p
          );
        }
        return false;
      }
    );
  }

  calculateShowTopdownLocationPicture() {
    if (
      this.client ||
      this.inputProject == null ||
      !CollectionHelper.isPopulated(this.inputProject.products)
    ) {
      this.showTopdownLocationPhoto = false;
      return;
    }
    this.showTopdownLocationPhoto = this.inputProject.products.some(
      (p) => ProductHelper.isBoard(p) || ProductHelper.isOtherProduct(p)
    );
  }

  calculateShowFloorQuestion(): void {
    if (
      this.inputProject == null ||
      !CollectionHelper.isPopulated(this.inputProject.products)
    ) {
      this.showFloorQuestion = true;
      return;
    }
    this.showFloorQuestion = !this.inputProject.products.every((p) =>
      ProductHelper.productPreventsShowFloorQuestion(p)
    );
  }

  calculateAddressPano(): void {
    if (this.inputSubProject.projectPlacement.address == null) {
      return;
    }
    this.getStreetViewMetadataService
      .getStreetViewLink(this.inputSubProject.projectPlacement.address)
      .then((pano: string) => (this.addressPanoId = pano))
      .catch((err) => console.error(err));
  }

  onSitePhotoUpload(): void {
    this.calculateSitePhotosAdded();
    this.projectDisplayObservableService
      .getProjectPlacementAssetsChangedSubscribable()
      .next(this.inputSubProject.projectPlacement.id);
  }

  onSitePhotoDeleted(): void {
    this.calculateSitePhotosAdded();
    this.projectDisplayObservableService
      .getProjectPlacementAssetsChangedSubscribable()
      .next(this.inputSubProject.projectPlacement.id);
  }

  calculateSitePhotosAdded(): void {
    this.sitePhotosAdded = CollectionHelper.isPopulated(
      this.inputSubProject.projectPlacement.projectPlacementAssets
    )
      ? this.inputSubProject.projectPlacement.projectPlacementAssets.some(
          (pa) => !pa.archived && pa.formSection === 'sitePhotos'
        )
      : false;
  }

  private calculateAlwaysShowCollectKey(): void {
    this.alwaysShowCollectKey = ProjectHelper.alwaysShowCollectKeyForProject(
      this.inputProject
    );
  }

  calculateIPBoardForPoleColor(): void {
    this.ipBoardForPoleColor = ProductHelper.getIPBoardForPoleColor(
      this.inputProject
    );
  }

  calculateShowExtrasSection() {
    this.showExtrasSection =
      !this.fromOldSystem &&
      this.inputProject != null &&
      CollectionHelper.isPopulated(this.inputProject.products) &&
      this.inputProject.products.some((p) => ProductHelper.isIPBoard(p));
  }

  calculateProjectHasIPBoard(): void {
    this.projectHasIPBoard =
      CollectionHelper.isPopulated(this.inputProject.products) &&
      this.inputProject.products.some((p) => ProductHelper.isIPBoard(p));
  }

  calculateIPBoardPartForFloorFiltering(): void {
    if (CollectionHelper.isPopulated(this.inputProject.products)) {
      const ipBoard = this.inputProject.products.find((p) =>
        ProductHelper.isIPBoard(p)
      );
      this.ipBoardPartForFloorFiltering =
        ipBoard != null ? (<IPBoard>ipBoard).boardPart : null;
    } else {
      this.ipBoardPartForFloorFiltering = null;
    }
  }

  compareValueEnum(o1: any, o2: any): boolean {
    return GeneralHelper.compareValueEnum(o1, o2);
  }

  calculatePlacementFloorReset(): void {
    if (this.projectHasIPBoard) {
      const ipBoardProds = this.inputProject.products.filter((p) =>
        ProductHelper.isIPBoard(p)
      );
      const floorReset = ipBoardProds.every((p) => {
        const ipBoard = <IPBoard>p;
        return (
          ipBoard.boardPart != null &&
          ipBoard.boardPart.placementType != null &&
          (ipBoard.boardPart.placementType.ordinal ===
            IPBoardPlacementTypeChoices.ON_POSTS.ordinal ||
            ipBoard.boardPart.placementType.ordinal ===
              IPBoardPlacementTypeChoices.ON_STONES.ordinal)
        );
      });
      if (floorReset) {
        this.inputProjectPlacement.placementFloor = null;
        this.emit();
      }
    }
  }

  daysFilter = (d: moment.Moment): boolean => {
    if (d != null) {
      const day = d.isoWeekday();
      return (
        !this.client ||
        (d.isSameOrAfter(this.minPlacementDate) && day !== 6 && day !== 7)
      );
    }
    return false;
  };
}
